import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  TextField,
  MenuItem,
  Chip,
  InputAdornment,
  Button,
  Tabs,
  Tab,
  Drawer,
  Divider,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";
import { apiRequest } from "../api/apiClient";

type Demo = {
  id: string;
  name: string;
  email: string;
  phone: string;
  company?: string;
  country?: string;
  machineType?: string;
  source?: string;
  status: "NEW" | "IN_PROGRESS" | "CLOSED";
  message?: string;
};

type TimelineItem = {
  status: string;
  message: string;
  created_at: string;
};

const statusColors: Record<string, string> = {
  NEW: "#0284c7",
  CONTACTED: "#f59e0b",
  IN_PROGRESS: "#2563eb",
  CLOSED: "#16a34a",
};

const statusOptions = ["NEW", "IN_PROGRESS", "CLOSED"];

export default function AdminPage() {
  const [data, setData] = useState<Demo[]>([]);
  const [editedData, setEditedData] = useState<Record<string, Partial<Demo>>>(
    {},
  );
  const [saveStatus, setSaveStatus] = useState<Record<string, string>>({});
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("ALL");

  const [tab, setTab] = useState(0);

  const [demoCount, setDemoCount] = useState(0);
  const [contactCount, setContactCount] = useState(0);

  const [selectedLead, setSelectedLead] = useState<Demo | null>(null);
  const [timelineOpen, setTimelineOpen] = useState(false);

  const [timelineData, setTimelineData] = useState<TimelineItem[]>([]);

  const fetchData = async () => {
    try {
      let res;

      if (tab === 0) {
        res = await apiRequest("/admin/demos");
        const list = Array.isArray(res) ? res : res.data || [];
        setData(list);
        setDemoCount(list.length);
      } else {
        res = await apiRequest("/admin/contacts");
        const list = Array.isArray(res) ? res : res.data || [];
        setData(list);
        setContactCount(list.length);
      }
    } catch (err) {
      console.error("Error fetching data", err);
    }
  };

  const fetchCounts = async () => {
    try {
      const [demoRes, contactRes] = await Promise.all([
        apiRequest("/admin/demos"),
        apiRequest("/admin/contacts"),
      ]);

      const demoList = Array.isArray(demoRes) ? demoRes : demoRes.data || [];
      const contactList = Array.isArray(contactRes)
        ? contactRes
        : contactRes.data || [];

      setDemoCount(demoList.length);
      setContactCount(contactList.length);
    } catch (err) {
      console.error("Error fetching counts", err);
    }
  };

  useEffect(() => {
    fetchCounts();
  }, []);

  useEffect(() => {
    fetchData();
  }, [tab]);

  const handleChange = (id: string, field: keyof Demo, value: string) => {
    setEditedData((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  };

  const handleSave = async (id: string) => {
    const updates = editedData[id];
    const current = data.find((d) => d.id === id);

    if (!updates || !current) return;

    const payload = {
      status: updates.status || current.status,
      message: updates.message ?? current.message ?? "",
    };

    try {
      const endpoint =
        tab === 0 ? `/admin/demos/${id}` : `/admin/contacts/${id}`;

      await apiRequest(endpoint, {
        method: "PUT",
        body: payload,
      });

      setData((prev) =>
        prev.map((d) =>
          d.id === id
            ? {
                ...d,
                status: payload.status,
              }
            : d,
        ),
      );

      setEditedData((prev) => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });

      setSaveStatus((prev) => ({
        ...prev,
        [id]: "Saved successfully",
      }));
      setTimeout(() => {
        setSaveStatus((prev) => {
          const copy = { ...prev };
          delete copy[id];
          return copy;
        });
      }, 4000);
    } catch (err) {
      setSaveStatus((prev) => ({
        ...prev,
        [id]: "Save failed",
      }));
      setTimeout(() => {
        setSaveStatus((prev) => {
          const copy = { ...prev };
          delete copy[id];
          return copy;
        });
      }, 4000);
    }
  };

  const openTimeline = async (item: Demo) => {
    setSelectedLead(item);
    setTimelineOpen(true);

    try {
      const endpoint =
        tab === 0
          ? `/admin/demos/${item.id}/history`
          : `/admin/contacts/${item.id}/history`;

      const res = await apiRequest(endpoint);
      const list = Array.isArray(res) ? res : res.data || [];
      setTimelineData(list);
    } catch {
      setTimelineData([]);
    }
  };

  const filteredData = useMemo(() => {
    return data.filter((d) => {
      const matchesSearch =
        d.name.toLowerCase().includes(search.toLowerCase()) ||
        d.email.toLowerCase().includes(search.toLowerCase());

      const matchesFilter = filter === "ALL" || d.status === filter;

      return matchesSearch && matchesFilter;
    });
  }, [data, search, filter]);

  const demoStats = useMemo(() => {
    if (tab !== 0) return null;

    const total = data.length;
    const closed = data.filter((d) => d.status === "CLOSED").length;

    return {
      total,
      NEW: data.filter((d) => d.status === "NEW").length,
      IN_PROGRESS: data.filter((d) => d.status === "IN_PROGRESS").length,
      CLOSED: closed,
      conversion: total === 0 ? 0 : Math.round((closed / total) * 100),
    };
  }, [data, tab]);

  return (
    <Box sx={{ background: "#f6f9fc", minHeight: "100vh" }}>
      <Container maxWidth="lg" sx={{ py: 16 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography fontSize={28} fontWeight={700}>
            Admin Dashboard
          </Typography>

          <Button
            variant="outlined"
            color="error"
            onClick={() => {
              localStorage.removeItem("token");
              window.location.href = "/login";
            }}
          >
            Logout
          </Button>
        </Box>

        {/* TABS */}
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
          <Tab label={`Demo Requests (${demoCount})`} />
          <Tab label={`Contact Requests (${contactCount})`} />
        </Tabs>

        {/* DEMO STATS */}
        {tab === 0 && demoStats && (
          <>
            <Grid container spacing={2} mb={3}>
              {[
                { label: "Total", value: demoStats.total },
                { label: "New", value: demoStats.NEW },
                { label: "In Progress", value: demoStats.IN_PROGRESS },
                { label: "Closed", value: demoStats.CLOSED },
                { label: "Conversion %", value: `${demoStats.conversion}%` },
              ].map((s, i) => (
                <Grid key={i} size={{ xs: 12, sm: 2.4 }}>
                  <Paper sx={{ p: 2 }}>
                    <Typography fontSize={13} color="gray">
                      {s.label}
                    </Typography>
                    <Typography fontSize={20} fontWeight={700}>
                      {s.value}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* MINI CHART */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography fontWeight={600} mb={2}>
                Lead Status Distribution
              </Typography>

              <Grid container spacing={2}>
                {["NEW", "IN_PROGRESS", "CLOSED"].map((status) => {
                  const count =
                    demoStats[status as keyof typeof demoStats] || 0;
                  const percentage =
                    demoStats.total === 0
                      ? 0
                      : Math.round((count / demoStats.total) * 100);

                  return (
                    <Grid key={status} size={{ xs: 12, sm: 4 }}>
                      <Typography fontSize={13}>{status}</Typography>

                      <Box sx={{ height: 10, bgcolor: "#eee", mt: 1 }}>
                        <Box
                          sx={{
                            width: `${percentage}%`,
                            height: "100%",
                            bgcolor:
                              status === "NEW"
                                ? "#0284c7"
                                : status === "IN_PROGRESS"
                                  ? "#2563eb"
                                  : "#16a34a",
                          }}
                        />
                      </Box>

                      <Typography fontSize={12} mt={1}>
                        {count} ({percentage}%)
                      </Typography>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          </>
        )}

        {/* FILTERS */}
        <Grid container spacing={2} mb={3}>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField
              select
              fullWidth
              size="small"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <MenuItem value="ALL">ALL</MenuItem>
              {statusOptions.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        {/* LIST */}
        <Grid container spacing={2}>
          {filteredData.map((item) => {
            const edited = editedData[item.id] || {};

            return (
              <Grid size={{ xs: 12 }} key={item.id}>
                <Paper sx={{ p: 3, borderRadius: 2 }}>
                  <Grid container spacing={2}>
                    {(() => {
                      const entries = Object.entries(item).filter(
                        ([key, value]) =>
                          !["id", "message", "updated_at", "status"].includes(
                            key,
                          ) &&
                          value !== null &&
                          value !== undefined &&
                          value !== "",
                      );

                      const mid = Math.ceil(entries.length / 2);
                      const leftItems = entries.slice(0, mid);
                      const rightItems = entries.slice(mid);

                      return (
                        <>
                          {/* LEFT SIDE */}
                          <Grid size={{ xs: 12, sm: 6 }}>
                            {leftItems.map(([key, value]) => {
                              const label = key
                                .replace(/_/g, " ")
                                .replace(/\b\w/g, (c) => c.toUpperCase());

                              return (
                                <Typography key={key} sx={{ mb: 1 }}>
                                  <b>{label}:</b>{" "}
                                  {key.includes("created") ||
                                  key.includes("updated")
                                    ? new Date(
                                        (value as string).replace(" ", "T"),
                                      ).toLocaleString("en-IN", {
                                        timeZone: "Asia/Kolkata",
                                      })
                                    : String(value)}
                                </Typography>
                              );
                            })}
                          </Grid>

                          {/* RIGHT SIDE */}
                          <Grid size={{ xs: 12, sm: 6 }}>
                            {/* STATUS */}
                            <Box sx={{ mb: 1 }}>
                              <Typography
                                component="span"
                                sx={{ fontWeight: "bold", mr: 1 }}
                              >
                                Status:
                              </Typography>
                              <Chip
                                label={item.status}
                                sx={{
                                  color: "#fff",
                                  fontWeight: 600,
                                  bgcolor:
                                    item.status === "CLOSED"
                                      ? "#16a34a"
                                      : item.status === "IN_PROGRESS"
                                        ? "#2563eb"
                                        : item.status === "NEW"
                                          ? "rgb(2, 132, 199)"
                                          : "#f59e0b",
                                }}
                              />
                            </Box>

                            {/* OTHER FIELDS */}
                            {rightItems.map(([key, value]) => {
                              const label = key
                                .replace(/_/g, " ")
                                .replace(/\b\w/g, (c) => c.toUpperCase());

                              return (
                                <Typography key={key} sx={{ mb: 1 }}>
                                  <b>{label}:</b>{" "}
                                  {key.includes("created") ||
                                  key.includes("updated")
                                    ? new Date(
                                        value.replace(" ", "T"),
                                      ).toLocaleString("en-IN", {
                                        timeZone: "Asia/Kolkata",
                                      })
                                    : String(value)}
                                </Typography>
                              );
                            })}
                          </Grid>
                        </>
                      );
                    })()}
                  </Grid>

                  {/* ✅ NEW: CUSTOMER MESSAGE (READ-ONLY) */}
                  {tab === 1 && item.message && (
                    <Typography sx={{ mt: 2 }}>
                      <b>Customer Message:</b> {item.message}
                    </Typography>
                  )}

                  <Grid container spacing={2} mt={2}>
                    <Grid size={{ xs: 12, sm: 3 }}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={edited.status || item.status}
                        onChange={(e) =>
                          handleChange(item.id, "status", e.target.value)
                        }
                      >
                        {statusOptions.map((s) => (
                          <MenuItem key={s} value={s}>
                            {s}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    {/* ✅ CHANGED: ADMIN MESSAGE FIELD ONLY */}
                    <Grid size={{ xs: 12, sm: 5 }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Admin note..."
                        value={edited.message ?? ""}
                        onChange={(e) =>
                          handleChange(item.id, "message", e.target.value)
                        }
                      />
                    </Grid>

                    {/*  NEW BUTTON */}
                    <Grid size={{ xs: 12, sm: 2 }}>
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => openTimeline(item)}
                      >
                        View Timeline
                      </Button>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 2 }}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => handleSave(item.id)}
                        disabled={!editedData[item.id]}
                      >
                        Save
                      </Button>
                    </Grid>

                    {/* ✅ ONLY CHANGE ADDED BELOW */}
                    {saveStatus[item.id] && (
                      <Grid size={{ xs: 12 }}>
                        <Typography
                          sx={{
                            mt: 1,
                            px: 2,
                            py: 1,
                            borderRadius: 1,
                            fontSize: 13,
                            fontWeight: 500,
                            bgcolor: saveStatus[item.id].includes("failed")
                              ? "#fee2e2"
                              : "#dcfce7",
                            color: saveStatus[item.id].includes("failed")
                              ? "#991b1b"
                              : "#166534",
                          }}
                        >
                          {saveStatus[item.id]}
                        </Typography>
                      </Grid>
                    )}
                    {/* ✅ END CHANGE */}
                  </Grid>
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        {/* TIMELINE */}
        <Drawer
          anchor="right"
          open={timelineOpen}
          onClose={() => setTimelineOpen(false)}
        >
          <Box sx={{ width: 380, p: 3 }}>
            <Typography fontSize={20} fontWeight={700} mb={2}>
              Lead Timeline
            </Typography>

            {selectedLead && (
              <>
                <Typography fontWeight={600}>{selectedLead.name}</Typography>
                <Typography fontSize={13} mb={2}>
                  {selectedLead.email}
                </Typography>

                <Divider sx={{ mb: 3 }} />

                <Box sx={{ position: "relative", pl: 4 }}>
                  {/* Vertical Line */}
                  <Box
                    sx={{
                      position: "absolute",
                      left: 10,
                      top: 0,
                      bottom: 0,
                      width: "2px",
                      bgcolor: "#e5e7eb",
                    }}
                  />

                  {timelineData.map((t, i) => {
                    const colorMap: any = {
                      NEW: "#0284c7",
                      CONTACTED: "#f59e0b",
                      IN_PROGRESS: "#2563eb",
                      CLOSED: "#16a34a",
                    };

                    const color = colorMap[t.status] || "#6b7280";

                    return (
                      <Box
                        key={i}
                        sx={{
                          position: "relative",
                          mb: 4,
                          display: "flex",
                          alignItems: "flex-start",
                        }}
                      >
                        {/* Dot */}
                        <Box
                          sx={{
                            position: "absolute",
                            left: 2,
                            top: 6,
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: color,
                            boxShadow: "0 0 0 3px #fff, 0 0 0 5px #e5e7eb",
                          }}
                        />

                        {/* Content */}
                        <Box sx={{ ml: 3 }}>
                          <Typography
                            fontSize={14}
                            fontWeight={600}
                            color={color}
                          >
                            {t.status}
                          </Typography>

                          <Typography fontSize={12} color="gray">
                            {new Date(
                              t.created_at.replace(" ", "T"),
                            ).toLocaleString("en-US", {
                              timeZone: "Asia/Kolkata",
                            })}
                          </Typography>

                          {t.message && (
                            <Typography fontSize={13} mt={0.5}>
                              {t.message}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </>
            )}
          </Box>
        </Drawer>
      </Container>
    </Box>
  );
}
